class UGCCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.items = [];
    this.isAnimating = false;
    this.autoPlayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    
    // Performance optimizations
    this.isMobile = window.innerWidth <= 768;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isLowEndDevice = this.detectLowEndDevice();
    this.lowPowerMode = false; // Will be set from data attribute
    this.preloadVideos = true; // Will be set from data attribute
    this.animationFrame = null;
    this.resizeTimer = null;
    
    // Scroll management
    this.pauseOnScroll = true; // Will be set from data attribute
    
    // Track manual pause state to prevent conflicts with autoplay
    this.manuallyPausedVideos = new Set();
    
    this.init();
  }
  
  detectLowEndDevice() {
    // Detect low-end devices based on available indicators
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const memory = navigator.deviceMemory;
    const cores = navigator.hardwareConcurrency;
    
    // Consider device low-end if:
    // - Less than 4GB RAM OR
    // - Less than 4 CPU cores OR 
    // - Slow connection
    return (
      (memory && memory < 4) ||
      (cores && cores < 4) ||
      (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g' || connection.effectiveType === '3g'))
    );
  }

  init() {
    // Read configuration from data attributes
    this.lowPowerMode = this.getAttribute('data-low-power-mode') === 'true';
    this.preloadVideos = this.getAttribute('data-preload-videos') === 'true';
    this.pauseOnScroll = this.getAttribute('data-pause-on-scroll') === 'true';
    
    // Override detection if low power mode is manually enabled
    if (this.lowPowerMode) {
      this.isLowEndDevice = true;
    }
    
    this.setupElements();
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.updateCarousel();
    
    if (this.getAttribute('data-auto-play') === 'true') {
      this.startAutoPlay();
    }
  }

  setupElements() {
    this.track = this.querySelector('.ugc-carousel__track');
    this.items = Array.from(this.querySelectorAll('.ugc-carousel__item'));
    this.dots = Array.from(this.querySelectorAll('.ugc-carousel__dot'));
    this.prevBtn = this.querySelector('.ugc-carousel__nav-btn--prev');
    this.nextBtn = this.querySelector('.ugc-carousel__nav-btn--next');
    this.videos = Array.from(this.querySelectorAll('.ugc-carousel__video'));
    
    // Apply performance classes based on device capabilities
    this.applyPerformanceClasses();
    
    // Set initial positions
    this.items.forEach((item, index) => {
      item.setAttribute('data-position', this.getRelativePosition(index, 0));
    });
  }
  
  applyPerformanceClasses() {
    // Add low-end device class if detected
    if (this.isLowEndDevice) {
      this.classList.add('low-end-device');
      this.track.classList.add('low-end-device');
      this.items.forEach(item => item.classList.add('low-end-device'));
      
      // Also apply to control elements
      const controls = this.querySelectorAll('.ugc-carousel__mute-btn, .ugc-carousel__play-pause-btn');
      controls.forEach(control => control.classList.add('low-end-device'));
      
      const frames = this.querySelectorAll('.ugc-carousel__frame');
      frames.forEach(frame => frame.classList.add('low-end-device'));
      
      const storyBars = this.querySelectorAll('.ugc-carousel__story-bar');
      storyBars.forEach(bar => bar.classList.add('low-end-device'));
    }
    
    // Optimize video settings for mobile/low-end devices
    if (this.isMobile || this.isLowEndDevice) {
      this.videos.forEach(video => {
        // Set preload based on user preference and device capability
        const preloadSetting = this.preloadVideos && !this.isLowEndDevice ? 'metadata' : 'none';
        video.setAttribute('preload', preloadSetting);
        video.setAttribute('playsinline', 'true');
        
        // Disable video on very low-end devices
        if (this.isLowEndDevice) {
          video.setAttribute('poster', video.getAttribute('poster') || '');
          // Convert to image if extremely low-end
          if (navigator.deviceMemory && navigator.deviceMemory < 2) {
            this.replaceVideoWithPoster(video);
          }
        }
      });
    }
  }
  
  replaceVideoWithPoster(video) {
    // For extremely low-end devices, replace videos with poster images
    const poster = video.getAttribute('poster');
    if (poster) {
      const img = document.createElement('img');
      img.src = poster;
      img.className = video.className.replace('ugc-carousel__video', 'ugc-carousel__fallback');
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      video.parentNode.replaceChild(img, video);
    }
  }

  setupEventListeners() {
    // Navigation buttons
    this.prevBtn?.addEventListener('click', () => this.previous());
    this.nextBtn?.addEventListener('click', () => this.next());

    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Touch/mouse events for swiping
    this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e));
    this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    
    this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.track.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.track.addEventListener('mouseleave', (e) => this.handleMouseUp(e));

    // Video controls
    this.videos.forEach(video => {
      this.setupVideoControls(video);
    });

    // Pause on hover
    if (this.getAttribute('data-pause-on-hover') === 'true') {
      this.addEventListener('mouseenter', () => this.pauseAutoPlay());
      this.addEventListener('mouseleave', () => this.resumeAutoPlay());
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Throttled window resize for better performance
    window.addEventListener('resize', () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.isMobile = window.innerWidth <= 768;
        this.updateCarousel();
      }, 150);
    });

    // Pause on scroll functionality is now handled by the intersection observer
  }

  setupVideoControls(video) {
    const container = video.closest('.ugc-carousel__item');
    const playPauseBtn = container.querySelector('.ugc-carousel__play-pause-btn');
    const muteBtn = container.querySelector('.ugc-carousel__mute-btn');

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleVideoPlayPause(video);
      }, { passive: false });
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleVideoMute(video);
      }, { passive: false });
    }

    // Video events with passive listeners for better performance
    video.addEventListener('ended', () => this.handleVideoEnded(video), { passive: true });
    video.addEventListener('loadedmetadata', () => {
      this.updateVideoControls(video);
      if (!this.isLowEndDevice) {
        this.initializeProgressBar(video);
      }
    }, { passive: true });
    video.addEventListener('play', () => {
      this.updateVideoControls(video);
      if (!this.isLowEndDevice) {
        this.startProgressTracking(video);
      }
    }, { passive: true });
    video.addEventListener('pause', () => {
      this.updateVideoControls(video);
      if (!this.isLowEndDevice) {
        this.pauseProgressTracking(video);
      }
    }, { passive: true });
    
    // Throttle timeupdate events for better performance
    if (!this.isLowEndDevice) {
      let progressUpdateTimer;
      video.addEventListener('timeupdate', () => {
        if (progressUpdateTimer) return;
        progressUpdateTimer = setTimeout(() => {
          this.updateProgressBar(video);
          progressUpdateTimer = null;
        }, 100); // Update every 100ms instead of every frame
      }, { passive: true });
    }
    
    video.addEventListener('loadstart', () => {
      if (!this.isLowEndDevice) {
        this.resetProgressBar(video);
      }
    }, { passive: true });
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.resumeAutoPlay();
        } else {
          this.pauseAutoPlay();
          // If pause on scroll is enabled, pause all videos when section leaves viewport
          if (this.pauseOnScroll) {
            this.pauseAllVideosOnScroll();
          }
        }
      });
    }, options);

    this.observer.observe(this);
  }

  pauseAllVideosOnScroll() {
    // Pause all videos in the carousel when scrolling out of viewport
    this.videos.forEach(video => {
      if (!video.paused) {
        video.pause();
        // Update video controls to reflect paused state
        this.updateVideoControls(video);
      }
    });
  }


  getRelativePosition(itemIndex, currentIndex) {
    let position = itemIndex - currentIndex;
    const totalItems = this.items.length;
    
    // Handle circular positioning
    if (position > totalItems / 2) {
      position -= totalItems;
    } else if (position < -totalItems / 2) {
      position += totalItems;
    }
    
    return position;
  }

  updateCarousel() {
    if (this.isAnimating) return;
    
    // Use requestAnimationFrame for better performance
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    this.animationFrame = requestAnimationFrame(() => {
      this.performCarouselUpdate();
    });
  }
  
  performCarouselUpdate() {
    const rotationAngle = this.isMobile || this.isLowEndDevice ? 15 : parseInt(this.getAttribute('data-rotation-angle')) || 25;
    const animationSpeed = this.isLowEndDevice ? 200 : parseInt(this.getAttribute('data-animation-speed')) || 400;
    
    // Batch DOM operations
    const updates = [];
    
    this.items.forEach((item, index) => {
      const position = this.getRelativePosition(index, this.currentIndex);
      const isCenter = position === 0;
      const isAdjacent = Math.abs(position) === 1;
      const isVisible = Math.abs(position) <= (this.isMobile ? 1 : 2); // Show fewer items on mobile
      
      // Simplified transforms for mobile/low-end devices
      let transform, opacity, zIndex;
      
      if (this.isMobile || this.isLowEndDevice) {
        // Simplified 2D transforms for better performance
        const translateX = position * 100; // Reduced spacing
        const scale = isCenter ? 1 : 0.8;
        opacity = isVisible ? (isCenter ? 1 : 0.5) : 0;
        transform = `translateX(${translateX}%) scale(${scale})`;
        zIndex = isCenter ? 10 : 1;
      } else {
        // Full 3D transforms for desktop
        const translateX = position * 450;
        const translateZ = isCenter ? 0 : -200 - (Math.abs(position) * 100);
        const rotateY = position * rotationAngle;
        const scale = isCenter ? 1 : (isAdjacent ? 0.8 : 0.6);
        opacity = isVisible ? (isCenter ? 1 : 0.7) : 0;
        transform = `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`;
        zIndex = isCenter ? 10 : (isAdjacent ? 5 : 1);
      }
      
      updates.push({
        item,
        transform,
        opacity,
        zIndex,
        position,
        isVisible,
        isCenter,
        animationSpeed
      });
    });
    
    // Apply all updates in batch
    updates.forEach(({ item, transform, opacity, zIndex, position, isVisible, isCenter, animationSpeed }) => {
      // Only update if values changed
      if (item.style.transform !== transform) {
        item.style.transform = transform;
      }
      if (item.style.opacity !== opacity.toString()) {
        item.style.opacity = opacity;
      }
      if (item.style.zIndex !== zIndex.toString()) {
        item.style.zIndex = zIndex;
      }
      
      // Simplified transition for low-end devices
      const transition = this.isLowEndDevice 
        ? `transform ${animationSpeed}ms ease-out, opacity ${animationSpeed}ms ease-out`
        : `all ${animationSpeed}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      
      if (item.style.transition !== transition) {
        item.style.transition = transition;
      }
      
      // Update data attributes
      item.setAttribute('data-position', position);
      item.setAttribute('aria-hidden', !isVisible);
      
      // Handle video playback - optimized for mobile
      this.handleVideoForItem(item, isCenter);
    });
    
    // Update UI elements
    this.updateNavigationElements();
  }
  
  handleVideoForItem(item, isCenter) {
    const video = item.querySelector('.ugc-carousel__video');
    const videosContainer = item.querySelector('.ugc-carousel__videos');
    
    if (!video || !videosContainer) return;
    
    if (isCenter) {
      // Only preload and play videos for center item
      const videosData = videosContainer.getAttribute('data-videos');
      if (videosData) {
        const videos = videosData.split(',').filter(v => v.trim());
        if (videos.length > 0) {
          // Lazy load video source
          this.loadVideoSource(video, videos[0]);
          videosContainer.setAttribute('data-current-video', '0');
          this.updateStoryIndicators(item, 0);
        }
      }
      
      // Sempre reproduzir vídeo automaticamente quando slide estiver ativo
      // Remove o vídeo do tracking de pause manual quando slide fica ativo
      this.manuallyPausedVideos.delete(video);
      video.currentTime = 0;
      video.play().catch(() => {}); // Ignore autoplay restrictions
    } else {
      // Pause and unload non-center videos to save memory
      video.pause();
      video.currentTime = 0;
      this.resetProgressBar(video);
      videosContainer.setAttribute('data-current-video', '0');
      
      // Unload video source on low-end devices to save memory
      if (this.isLowEndDevice) {
        video.removeAttribute('src');
        video.load();
      }
    }
  }
  
  loadVideoSource(video, src) {
    // Only load if src is different
    const currentSrc = video.src || (video.querySelector('source') && video.querySelector('source').src);
    if (currentSrc !== src) {
      const videoElement = video.querySelector('source') || video;
      videoElement.src = src;
      video.load();
    }
  }
  
  updateNavigationElements() {
    // Update dots
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive);
    });
    
    // Update navigation buttons
    if (this.prevBtn) {
      this.prevBtn.setAttribute('aria-disabled', 'false');
    }
    if (this.nextBtn) {
      this.nextBtn.setAttribute('aria-disabled', 'false');
    }
  }

  next() {
    if (this.isAnimating) return;
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.updateCarousel();
    this.handleSlideChange();
  }

  previous() {
    if (this.isAnimating) return;
    this.currentIndex = this.currentIndex === 0 ? this.items.length - 1 : this.currentIndex - 1;
    this.updateCarousel();
    this.handleSlideChange();
  }

  goToSlide(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    this.currentIndex = index;
    this.updateCarousel();
    this.handleSlideChange();
  }

  handleSlideChange() {
    this.isAnimating = true;
    const animationSpeed = parseInt(this.getAttribute('data-animation-speed')) || 400;
    
    setTimeout(() => {
      this.isAnimating = false;
    }, animationSpeed);

    // Reset auto-play timer
    if (this.autoPlayInterval) {
      this.startAutoPlay();
    }
  }

  startAutoPlay() {
    // Disable autoplay on low-end devices to save resources
    if (this.isLowEndDevice) {
      return;
    }
    
    this.pauseAutoPlay();
    const speed = parseInt(this.getAttribute('data-auto-play-speed')) || 5;
    
    // Increase interval on mobile for better battery life
    const adjustedSpeed = this.isMobile ? speed * 1.5 : speed;
    
    this.autoPlayInterval = setInterval(() => {
      this.checkAndAdvanceCarousel();
    }, adjustedSpeed * 1000);
  }

  pauseAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  getCurrentCenterVideo() {
    // Obtém o vídeo do item central atual
    const centerItem = this.items[this.currentIndex];
    if (!centerItem) return null;
    
    return centerItem.querySelector('.ugc-carousel__video');
  }

  isCurrentVideoPlaying() {
    // Verifica se o vídeo atual está reproduzindo
    const video = this.getCurrentCenterVideo();
    if (!video) return false;
    
    return !video.paused && !video.ended && video.readyState > 2;
  }

  isCurrentVideoFinished() {
    // Verifica se o vídeo atual terminou ou se todos os vídeos do item atual terminaram
    const centerItem = this.items[this.currentIndex];
    if (!centerItem) return true;
    
    const videosContainer = centerItem.querySelector('.ugc-carousel__videos');
    if (!videosContainer) return true;
    
    const videosData = videosContainer.getAttribute('data-videos');
    const currentVideoIndex = parseInt(videosContainer.getAttribute('data-current-video')) || 0;
    
    if (videosData) {
      const videos = videosData.split(',').filter(v => v.trim());
      // Se é o último vídeo do item e terminou
      return currentVideoIndex >= videos.length - 1 && this.getCurrentCenterVideo()?.ended;
    }
    
    return this.getCurrentCenterVideo()?.ended || false;
  }

  checkAndAdvanceCarousel() {
    // Verifica estado do vídeo antes de avançar o carrossel
    const currentVideo = this.getCurrentCenterVideo();
    const isPlaying = this.isCurrentVideoPlaying();
    const isFinished = this.isCurrentVideoFinished();
    const isManuallyPaused = currentVideo && this.manuallyPausedVideos.has(currentVideo);
    
    // Se o vídeo está reproduzindo, não avança
    if (isPlaying) {
      return;
    }
    
    // Se o vídeo foi pausado manualmente pelo usuário, não avança
    if (isManuallyPaused) {
      return;
    }
    
    // Se o vídeo terminou ou está pausado automaticamente, avança
    if (isFinished || (!isPlaying && !isManuallyPaused)) {
      this.next();
    }
  }

  resumeAutoPlay() {
    if (this.getAttribute('data-auto-play') === 'true' && !this.autoPlayInterval) {
      this.startAutoPlay();
    }
  }

  // Touch event handlers - optimized for mobile
  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.isDragging = true;
    
    // Prevent auto-play during user interaction
    this.pauseAutoPlay();
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    // Calculate movement to determine if it's horizontal swipe
    const deltaX = Math.abs(e.touches[0].clientX - this.touchStartX);
    const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);
    
    // Only prevent default for horizontal swipes
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;
    this.touchEndX = e.changedTouches[0].clientX;
    this.handleSwipe();
    this.isDragging = false;
    
    // Resume auto-play after interaction
    setTimeout(() => this.resumeAutoPlay(), 1000);
  }

  // Mouse event handlers
  handleMouseDown(e) {
    this.touchStartX = e.clientX;
    this.isDragging = true;
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;
    this.touchEndX = e.clientX;
    this.handleSwipe();
    this.isDragging = false;
  }

  handleSwipe() {
    const swipeThreshold = this.isMobile ? 30 : 50; // Lower threshold for mobile
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) > swipeThreshold) {
      // Add haptic feedback on supported devices
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
      
      if (swipeDistance > 0) {
        this.previous();
      } else {
        this.next();
      }
    }
  }

  // Keyboard navigation
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        const centerVideo = this.items[this.currentIndex].querySelector('.ugc-carousel__video');
        if (centerVideo) {
          this.toggleVideoPlayPause(centerVideo);
        }
        break;
    }
  }

  // Video controls
  toggleVideoPlayPause(video) {
    if (video.paused) {
      // Remove from manually paused set when user plays manually
      this.manuallyPausedVideos.delete(video);
      video.play().catch(() => {});
    } else {
      // Add to manually paused set when user pauses manually
      this.manuallyPausedVideos.add(video);
      video.pause();
    }
  }

  toggleVideoMute(video) {
    video.muted = !video.muted;
    this.updateVideoControls(video);
  }

  updateVideoControls(video) {
    const container = video.closest('.ugc-carousel__item');
    const playPauseBtn = container.querySelector('.ugc-carousel__play-pause-btn');
    const muteBtn = container.querySelector('.ugc-carousel__mute-btn');
    
    if (playPauseBtn) {
      const playIcon = playPauseBtn.querySelector('.icon-play');
      const pauseIcon = playPauseBtn.querySelector('.icon-pause');
      
      if (video.paused) {
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
      } else {
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
      }
    }
    
    if (muteBtn) {
      const soundOffIcon = muteBtn.querySelector('.icon-sound-off');
      const soundOnIcon = muteBtn.querySelector('.icon-sound-on');
      
      if (video.muted) {
        soundOffIcon.style.display = 'block';
        soundOnIcon.style.display = 'none';
      } else {
        soundOffIcon.style.display = 'none';
        soundOnIcon.style.display = 'block';
      }
    }
  }

  handleVideoEnded(video) {
    const container = video.closest('.ugc-carousel__item');
    const videosContainer = video.closest('.ugc-carousel__videos');
    const videosData = videosContainer.getAttribute('data-videos');
    const playOrder = videosContainer.getAttribute('data-play-order');
    const currentVideoIndex = parseInt(videosContainer.getAttribute('data-current-video')) || 0;
    const totalVideos = parseInt(videosContainer.getAttribute('data-total-videos')) || 1;
    
    // Remove do tracking de pause manual quando o vídeo termina naturalmente
    if (video.ended) {
      this.manuallyPausedVideos.delete(video);
    }
    
    // Verifica se o vídeo foi pausado manualmente pelo usuário
    // Se foi pausado manualmente, não avança automaticamente
    if (video.paused && !video.ended) {
      return; // Não faz nada se foi pausado manualmente
    }
    
    if (videosData) {
      const videos = videosData.split(',').filter(v => v.trim());
      
      // Check if there are more videos in this item
      if (currentVideoIndex < videos.length - 1) {
        // Play next video in this item
        let nextIndex;
        
        if (playOrder === 'random') {
          do {
            nextIndex = Math.floor(Math.random() * videos.length);
          } while (nextIndex === currentVideoIndex && videos.length > 1);
        } else {
          nextIndex = currentVideoIndex + 1;
        }
        
        // Update video source and play
        const videoElement = video.querySelector('source') || video;
        videoElement.src = videos[nextIndex];
        video.load();
        
        // Update data attributes
        videosContainer.setAttribute('data-current-video', nextIndex);
        
        // Update story indicators
        this.updateStoryIndicators(container, nextIndex);
        
        // Play new video if this item is currently active
        const isCurrentItem = container.getAttribute('data-position') === '0';
        if (isCurrentItem) {
          // Remove from manually paused set since this is a new video in sequence
          this.manuallyPausedVideos.delete(video);
          video.play().catch(() => {});
        }
      } else {
        // All videos in this item finished, advance to next carousel item
        this.advanceToNextItem();
      }
    } else {
      // Single video finished, advance to next carousel item
      this.advanceToNextItem();
    }
  }

  advanceToNextItem() {
    // Only advance if auto-play is enabled
    if (this.getAttribute('data-auto-play') === 'true') {
      // Verifica se o vídeo atual está pausado manualmente ou se devemos avançar
      const currentVideo = this.getCurrentCenterVideo();
      const isManuallyPaused = currentVideo && this.manuallyPausedVideos.has(currentVideo);
      
      // Se não há vídeo ou se o vídeo NÃO foi pausado manualmente, avança
      if (!currentVideo || !isManuallyPaused) {
        setTimeout(() => {
          this.next();
        }, 500); // 500ms delay for better UX
      }
      // Se o vídeo foi pausado manualmente, não avança
    }
  }

  updateStoryIndicators(container, activeIndex) {
    const indicators = container.querySelectorAll('.ugc-carousel__story-bar');
    indicators.forEach((indicator, index) => {
      // Remove all state classes first
      indicator.classList.remove('active', 'completed', 'playing');
      
      if (index < activeIndex) {
        // Mark previous indicators as completed
        indicator.classList.add('completed');
        indicator.style.setProperty('--progress', '100%');
      } else if (index === activeIndex) {
        // Mark current indicator as active
        indicator.classList.add('active');
        indicator.style.setProperty('--progress', '0%');
      } else {
        // Reset future indicators
        indicator.style.setProperty('--progress', '0%');
      }
    });
  }

  initializeProgressBar(video) {
    const container = video.closest('.ugc-carousel__item');
    const videosContainer = video.closest('.ugc-carousel__videos');
    const currentVideoIndex = parseInt(videosContainer.getAttribute('data-current-video')) || 0;
    const activeIndicator = container.querySelector('.ugc-carousel__story-bar.active');
    
    if (activeIndicator && video.duration) {
      activeIndicator.style.setProperty('--progress', '0%');
      activeIndicator.style.setProperty('--duration', `${video.duration}s`);
    }
  }

  updateProgressBar(video) {
    const container = video.closest('.ugc-carousel__item');
    const activeIndicator = container.querySelector('.ugc-carousel__story-bar.active');
    
    if (activeIndicator && video.duration > 0) {
      const progress = (video.currentTime / video.duration) * 100;
      activeIndicator.style.setProperty('--progress', `${progress}%`);
    }
  }

  startProgressTracking(video) {
    const container = video.closest('.ugc-carousel__item');
    const activeIndicator = container.querySelector('.ugc-carousel__story-bar.active');
    
    if (activeIndicator) {
      activeIndicator.classList.add('playing');
    }
  }

  pauseProgressTracking(video) {
    const container = video.closest('.ugc-carousel__item');
    const activeIndicator = container.querySelector('.ugc-carousel__story-bar.active');
    
    if (activeIndicator) {
      activeIndicator.classList.remove('playing');
    }
  }

  resetProgressBar(video) {
    const container = video.closest('.ugc-carousel__item');
    const activeIndicator = container.querySelector('.ugc-carousel__story-bar.active');
    
    if (activeIndicator) {
      activeIndicator.style.setProperty('--progress', '0%');
      activeIndicator.classList.remove('playing');
    }
  }

  disconnectedCallback() {
    this.pauseAutoPlay();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // Clear manually paused videos tracking
    this.manuallyPausedVideos.clear();
    
    // Cleanup video sources on low-end devices
    if (this.isLowEndDevice) {
      this.videos.forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.load();
      });
    }
  }
}

// Register the custom element
if (!customElements.get('ugc-carousel')) {
  customElements.define('ugc-carousel', UGCCarousel);
}